import React, { useState } from 'react';
import { Package, MapPin, Layers, Target, ShoppingCart, Truck, AlertCircle, BarChart3, TrendingUp, ArrowLeftRight, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useScreenInit } from '../../useScreenInit.js';
// Sub-components
import { ItemMaster } from './inventory/ItemMaster';
import { LocationCount } from './inventory/LocationCount';
import { CategoryMasters } from './inventory/CategoryMasters';
import { ParManagement } from './inventory/ParManagement';
import { OrderGeneration } from './inventory/OrderGeneration';
import { ReceivingModule } from './inventory/ReceivingModule';
import { OosTracker } from './inventory/OosTracker';
import { ValuationReport } from './inventory/ValuationReport';
import { UsageReport } from './inventory/UsageReport';
import { Adjustments } from './inventory/Adjustments';
type InventoryTab = 'item-master' | 'location-count' | 'category-masters' | 'par-mgmt' | 'order-gen' | 'receiving' | 'oos-tracker' | 'valuation' | 'usage' | 'adjustments';
const tabs: {
  id: InventoryTab;
  label: string;
  desc?: string;
  icon: any;
}[] = [{
  id: 'item-master',
  label: 'Item Master',
  icon: Package
}, {
  id: 'location-count',
  label: 'Location Count',
  icon: MapPin
}, {
  id: 'category-masters',
  label: 'Category Masters',
  icon: Layers
}, {
  id: 'par-mgmt',
  label: 'PAR Management',
  icon: Target
}, {
  id: 'order-gen',
  label: 'Order Generation',
  icon: ShoppingCart
}, {
  id: 'receiving',
  label: 'Receiving Module',
  desc: 'Receive, verify, update inventory',
  icon: Truck
}, {
  id: 'oos-tracker',
  label: 'Out of Stock Tracker',
  desc: 'Track, resolve, analyze OOS',
  icon: AlertCircle
}, {
  id: 'valuation',
  label: 'Valuation Report',
  icon: BarChart3
}, {
  id: 'usage',
  label: 'Usage Report',
  icon: TrendingUp
}, {
  id: 'adjustments',
  label: 'Inventory Adjustments',
  desc: 'MIS PIC, damage, waste, transfers, returns',
  icon: ArrowLeftRight
}];
export function InventoryMgmtView() {
  const screenInit = useScreenInit();
  const [activeTab, setActiveTab] = useState<InventoryTab>(screenInit?.inventoryTab as InventoryTab || 'item-master');
  const [showMoreDropdown, setShowMoreDropdown] = useState(false);
  const moreTabs = tabs.slice(5);
  const isMoreTabActive = moreTabs.some(tab => tab.id === activeTab);
  const activeMoreTab = moreTabs.find(tab => tab.id === activeTab);
  return <div className="space-y-6" data-id="element-1180">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4" data-id="element-1181">
        <div data-id="element-1182">
          <h2 className="text-2xl font-bold text-slate-900" data-id="element-1183">
            Inventory Management System
          </h2>
          <p className="text-sm text-slate-500 font-medium" data-id="element-1184">
            Core module for stock control and procurement
          </p>
        </div>

        <div className="flex bg-white p-1 rounded-2xl border border-slate-200 shadow-sm" data-id="element-1185">
          {tabs.slice(0, 5).map(tab => <TabButton key={tab.id} active={activeTab === tab.id} onClick={() => setActiveTab(tab.id)} label={tab.label} icon={tab.icon} data-id="element-1186" />)}
          <div className="relative" data-id="element-1187">
            <button onClick={() => setShowMoreDropdown(!showMoreDropdown)} className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all ${isMoreTabActive ? 'bg-slate-900 text-white shadow-lg shadow-slate-900/20' : 'text-slate-500 hover:bg-slate-50'}`} data-id="element-1188">
              {isMoreTabActive ? activeMoreTab?.label : 'More'}{' '}
              <ChevronDown size={14} className={`transition-transform duration-200 ${showMoreDropdown ? 'rotate-180' : ''}`} data-id="element-1189" />
            </button>

            <AnimatePresence data-id="element-1190">
              {showMoreDropdown && <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowMoreDropdown(false)} data-id="element-1191" />
                  <motion.div initial={{
                opacity: 0,
                y: 10,
                scale: 0.95
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} exit={{
                opacity: 0,
                y: 10,
                scale: 0.95
              }} className="absolute right-0 top-full mt-2 w-64 bg-white rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.2)] border border-slate-100 py-2 z-[100] overflow-hidden" data-id="element-1192">
                    <div className="px-4 py-2 border-b border-slate-50 mb-1" data-id="element-1193">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-1194">
                        Additional Modules
                      </p>
                    </div>
                    {moreTabs.map(tab => <button key={tab.id} onClick={() => {
                  setActiveTab(tab.id);
                  setShowMoreDropdown(false);
                }} className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-all ${activeTab === tab.id ? 'bg-emerald-50' : 'hover:bg-slate-50'}`} data-id="element-1195">
                        <div className={`p-2 rounded-lg shrink-0 ${activeTab === tab.id ? 'bg-white text-emerald-600 shadow-sm' : 'bg-slate-100 text-slate-400'}`} data-id="element-1196">
                          <tab.icon size={16} data-id="element-1197" />
                        </div>
                        <div className="flex flex-col min-w-0" data-id="element-1198">
                          <span className={`text-xs font-bold truncate ${activeTab === tab.id ? 'text-emerald-700' : 'text-slate-700'}`} data-id="element-1199">
                            {tab.label}
                          </span>
                          {tab.desc && <span className="text-[10px] text-slate-400 font-medium leading-tight mt-0.5" data-id="element-1200">
                              {tab.desc}
                            </span>}
                        </div>
                      </button>)}
                  </motion.div>
                </>}
            </AnimatePresence>
          </div>
        </div>
      </div>

      <div className="min-h-[600px]" data-id="element-1201">
        <AnimatePresence mode="wait" data-id="element-1202">
          <motion.div key={activeTab} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.2
        }} data-id="element-1203">
            {activeTab === 'item-master' && <ItemMaster data-id="element-1204" />}
            {activeTab === 'location-count' && <LocationCount data-id="element-1205" />}
            {activeTab === 'category-masters' && <CategoryMasters data-id="element-1206" />}
            {activeTab === 'par-mgmt' && <ParManagement data-id="element-1207" />}
            {activeTab === 'order-gen' && <OrderGeneration data-id="element-1208" />}
            {activeTab === 'receiving' && <ReceivingModule data-id="element-1209" />}
            {activeTab === 'oos-tracker' && <OosTracker data-id="element-1210" />}
            {activeTab === 'valuation' && <ValuationReport data-id="element-1211" />}
            {activeTab === 'usage' && <UsageReport data-id="element-1212" />}
            {activeTab === 'adjustments' && <Adjustments data-id="element-1213" />}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>;
}
function TabButton({
  active,
  onClick,
  label,
  icon: Icon
}: any) {
  return <button onClick={onClick} className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap ${active ? 'bg-slate-900 text-white shadow-lg shadow-slate-900/20' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'}`} data-id="element-1214">
      <Icon size={14} data-id="element-1215" />
      {label}
    </button>;
}